import React, { useState } from 'react';
import { Card, InternalCard, Modal, Pill } from '../ui';
import { useCop } from '../CopApp';
import BrandGuide from './BrandGuide';
import {
	REPORT_TYPES,
	REPORT_CADENCES,
	CONFIDENCE_SETTINGS,
	ALLOCATION_MODES,
} from '../copData';

const REPORT_LABEL = Object.fromEntries(REPORT_TYPES.map((r) => [r.value, r.label]));
const CADENCE_LABEL = Object.fromEntries(REPORT_CADENCES.map((c) => [c.value, c.label]));

const EMPTY_REPORT = {
	name: '',
	report: REPORT_TYPES[0].value,
	cadence: 'weekly',
	recipients: '',
	format: 'pdf',
};

const TABS = [
	{ code: 'general', label: 'General' },
	{ code: 'reports', label: 'Scheduled Reports' },
	{ code: 'automation', label: 'Automation', internal: true },
	{ code: 'brand', label: 'Brand Guide' },
];

// Confidence slider — value stored as a 0–1 fraction, shown as a percentage.
function ConfidenceSlider({ setting, value, onChange }) {
	const min = setting.min ?? 0.5;
	return (
		<div className="cop-conf-setting">
			<div className="cop-conf-setting-head">
				<span className="k">{setting.label}</span>
				<span className={`cop-conf-setting-val tone-${setting.tone}`}>{Math.round(value * 100)}%</span>
			</div>
			<input
				type="range"
				min={Math.round(min * 100)}
				max={99}
				value={Math.round(value * 100)}
				onChange={(e) => onChange(setting.key, Number(e.target.value) / 100)}
				aria-label={setting.label}
			/>
			<div className="cop-conf-setting-hint">{setting.hint}</div>
		</div>
	);
}

export default function Settings() {
	const {
		isInternal,
		showToast,
		defaultDatePreset,
		updateDefaultDatePreset,
		reportDatePresets,
		defaultAllocationMode,
		setDefaultAllocationMode,
		defaultExperimentLength,
		setDefaultExperimentLength,
		defaultConfidencePct,
		setDefaultConfidencePct,
		autopilot,
		toggleAutopilot,
		autopilotRiskCeiling,
		setAutopilotRiskCeiling,
		confidence,
		updateConfidence,
		scheduledReports,
		addScheduledReport,
		updateScheduledReport,
		deleteScheduledReport,
	} = useCop();

	const [modalOpen, setModalOpen] = useState(false);
	const [draft, setDraft] = useState(EMPTY_REPORT);
	const tabs = TABS.filter((t) => !t.internal || isInternal);
	const [tab, setTab] = useState('general');
	const activeTab = tabs.some((t) => t.code === tab) ? tab : 'general';

	const openAdd = () => {
		setDraft(EMPTY_REPORT);
		setModalOpen(true);
	};

	const saveReport = () => {
		if (!draft.name.trim() || !draft.recipients.trim()) {
			showToast('Name and at least one recipient are required');
			return;
		}
		addScheduledReport(draft);
		setModalOpen(false);
	};

	return (
		<main className="cop-page">
			<div style={{ maxWidth: 760 }}>
				<h1 className="cop-page-title">Settings</h1>
				<div className="cop-page-sub">Account-wide defaults for reporting, scheduled delivery, automation, and brand.</div>
			</div>

			<div className="cop-builder-tabs cop-settings-tabs">
				{tabs.map((t) => (
					<button
						key={t.code}
						className={`cop-builder-tab${activeTab === t.code ? ' active' : ''}`}
						onClick={() => setTab(t.code)}
					>
						{t.label}
					</button>
				))}
			</div>

			{activeTab === 'general' && (
				<div className="cop-settings-grid">
					<Card title="Reporting Defaults" icon="◫">
						<div className="cop-config-row">
							<span className="k">Default date range</span>
							<select
								className="cop-select cop-config-select"
								value={defaultDatePreset}
								onChange={(e) => updateDefaultDatePreset(e.target.value)}
							>
								{reportDatePresets
									.filter((p) => p.value !== 'custom')
									.map((p) => (
										<option key={p.value} value={p.value}>
											{p.label}
										</option>
									))}
							</select>
						</div>
						<div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6, marginTop: 8 }}>
							The default applies to Command Center, Reporting, Experiments, Clustering, and the Audit Log when you open them. You can still
							change the range per view from the top bar.
						</div>
					</Card>

					<Card title="Experiment Defaults" icon="⚗">
						<div className="cop-config-row">
							<span className="k">Traffic allocation changes</span>
							<select
								className="cop-select cop-config-select"
								value={defaultAllocationMode}
								onChange={(e) => setDefaultAllocationMode(e.target.value)}
							>
								{ALLOCATION_MODES.map((m) => (
									<option key={m.value} value={m.value}>{m.label}</option>
								))}
							</select>
						</div>
						<div className="cop-config-row">
							<span className="k">Experiment length (days)</span>
							<input
								className="cop-input cop-config-select"
								type="number"
								min="1"
								max="365"
								value={defaultExperimentLength}
								onChange={(e) => setDefaultExperimentLength(e.target.value)}
							/>
						</div>
						<div className="cop-config-row">
							<span className="k">Confidence to declare (%)</span>
							<input
								className="cop-input cop-config-select"
								type="number"
								min="50"
								max="99"
								value={defaultConfidencePct}
								onChange={(e) => setDefaultConfidencePct(e.target.value)}
							/>
						</div>
						<div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6, marginTop: 8 }}>
							{ALLOCATION_MODES.find((m) => m.value === defaultAllocationMode)?.hint} New experiments start with these defaults; you can
							override them per experiment in the New Experiment dialog.
						</div>
					</Card>
				</div>
			)}

			{activeTab === 'reports' && (
				<div className="cop-settings-grid">
					<Card
						title="Scheduled Reports"
						icon="⏱"
						className="cop-settings-wide"
						right={<button className="cop-btn small" onClick={openAdd}>+ Schedule</button>}
					>
						{scheduledReports.length === 0 && (
							<div style={{ color: 'var(--text-dim)', fontSize: 12.5, padding: '10px 0' }}>
								No scheduled reports yet. Schedule one to deliver it automatically.
							</div>
						)}
						{scheduledReports.map((r) => (
							<div className="cop-sched-row" key={r.id}>
								<button
									className={`cop-toggle${r.enabled ? ' on' : ''}`}
									onClick={() => updateScheduledReport(r.id, { enabled: !r.enabled })}
									aria-label={`toggle ${r.name}`}
								/>
								<div className="cop-sched-body">
									<div className="cop-sched-name">{r.name}</div>
									<div className="cop-sched-meta">
										<Pill tone="dim">{REPORT_LABEL[r.report] || r.report}</Pill>
										<span>{CADENCE_LABEL[r.cadence] || r.cadence}</span>
										<span>·</span>
										<span className="cop-mono">{r.format.toUpperCase()}</span>
										<span>·</span>
										<span className="cop-sched-to">{r.recipients}</span>
									</div>
								</div>
								<button className="cop-icon-x" onClick={() => deleteScheduledReport(r.id)} aria-label={`remove ${r.name}`} title="Remove">
									✕
								</button>
							</div>
						))}
					</Card>
				</div>
			)}

			{activeTab === 'automation' && isInternal && (
				<div className="cop-settings-grid">
					<InternalCard title="Automation" icon="⚙">
						<div className="cop-config-row">
							<span className="k">Auto-Pilot</span>
							<button
								className={`cop-toggle${autopilot ? ' on' : ''}`}
								onClick={toggleAutopilot}
								aria-label="toggle Auto-Pilot"
							/>
						</div>
						<div className="cop-config-row">
							<span className="k">Auto-Pilot risk ceiling</span>
							<select
								className="cop-select cop-config-select"
								value={autopilotRiskCeiling}
								onChange={(e) => setAutopilotRiskCeiling(e.target.value)}
								disabled={!autopilot}
							>
								<option value="low">Low risk only</option>
								<option value="medium">Up to medium risk</option>
							</select>
						</div>
						<div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6, marginTop: 8 }}>
							Auto-Pilot executes only pre-authorized actions at or below the risk ceiling. Anything above it — and anything
							client-facing — still routes through the Approval Queue.
						</div>
					</InternalCard>

					<InternalCard title="Confidence Thresholds" icon="◎" className="cop-settings-wide">
						<div className="cop-conf-grid">
							{CONFIDENCE_SETTINGS.map((s) => (
								<ConfidenceSlider
									key={s.key}
									setting={s}
									value={confidence[s.key]}
									onChange={updateConfidence}
								/>
							))}
						</div>
					</InternalCard>
				</div>
			)}

			{activeTab === 'brand' && <BrandGuide />}

			{modalOpen && (
				<Modal
					title="Schedule a report"
					sub="Delivered automatically to the recipients on the chosen cadence."
					onClose={() => setModalOpen(false)}
					actions={
						<>
							<button className="cop-btn" onClick={() => setModalOpen(false)}>Cancel</button>
							<button className="cop-btn primary" onClick={saveReport}>Schedule</button>
						</>
					}
				>
					<div className="cop-form-grid">
						<label className="cop-form-field cop-form-span">
							<span>Name</span>
							<input
								className="cop-input"
								value={draft.name}
								onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
								placeholder="Weekly Executive Summary"
							/>
						</label>
						<label className="cop-form-field">
							<span>Report</span>
							<select className="cop-select" value={draft.report} onChange={(e) => setDraft((d) => ({ ...d, report: e.target.value }))}>
								{REPORT_TYPES.map((r) => (
									<option key={r.value} value={r.value}>{r.label}</option>
								))}
							</select>
						</label>
						<label className="cop-form-field">
							<span>Cadence</span>
							<select className="cop-select" value={draft.cadence} onChange={(e) => setDraft((d) => ({ ...d, cadence: e.target.value }))}>
								{REPORT_CADENCES.map((c) => (
									<option key={c.value} value={c.value}>{c.label}</option>
								))}
							</select>
						</label>
						<label className="cop-form-field">
							<span>Format</span>
							<select className="cop-select" value={draft.format} onChange={(e) => setDraft((d) => ({ ...d, format: e.target.value }))}>
								<option value="pdf">PDF</option>
								<option value="csv">CSV</option>
							</select>
						</label>
						<label className="cop-form-field cop-form-span">
							<span>Recipients (comma-separated)</span>
							<input
								className="cop-input"
								value={draft.recipients}
								onChange={(e) => setDraft((d) => ({ ...d, recipients: e.target.value }))}
								placeholder="growth@sideshow.com, ops@upsellit.com"
							/>
						</label>
					</div>
				</Modal>
			)}
		</main>
	);
}
